import {useFormik} from 'formik'
import {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import * as Yup from 'yup'
import {useCancelToken, UseYupValidate} from '../../../../gori/hooks'
import {Button} from '../../../../gori/widgets'
import {InputPassword} from '../../../../gori/widgets/form/InputPassword'
import {useAuth} from '../../../../gori/providers'
import {AuthService} from '../../auth'
import {CommonService} from '../../common'
import {formatApiFieldError} from '../core/helpers'

type TokenStatus = 'verifying' | 'invalid' | 'expired' | 'valid'

export function NewPassword() {
  const intl = useIntl()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const {newCancelToken, isCancel} = useCancelToken()
  const {authYup} = UseYupValidate()
  const {saveAuth, handleSaveCurrentUser} = useAuth()

  const [tokenStatus, setTokenStatus] = useState<TokenStatus>('verifying')
  // NOTE: gori-partner-api has no concept of trade show accounts, `verify-token`
  // never returns `trade_show_account`, so this is always `false` in practice.
  // Kept only for parity with the ShipBae flow (see AuthService.tradeShowNewPassword).
  const isTradeShowAccount = false
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!token) {
      setTokenStatus('invalid')
      return
    }

    const config = {cancelToken: newCancelToken()}
    ;(async () => {
      try {
        // axiosConfig's response interceptor already unwraps `response.data`,
        // so a 200 here resolves directly with `{ message: 'Valid token.' }`.
        await AuthService.verifyToken(token, config)
        setTokenStatus('valid')
      } catch (error: any) {
        if (isCancel(error)) return
        // gori-partner-api has no `fields.expired` flag (unlike the ShipBae
        // reference flow). The only way to tell "expired" apart from "invalid"
        // is the business error message returned by the API:
        // { status: 'ERROR', message: 'Invalid token!' | 'This token has expired!' }
        const message: string = error?.response?.data?.message || ''
        const expired = /expired/i.test(message)
        setTokenStatus(expired ? 'expired' : 'invalid')
      }
    })()
    // eslint-disable-next-line
  }, [token])

  const newPasswordSchema = Yup.object().shape({
    password: authYup.password(),
    password_confirmation: authYup.passwordConfirmation(),
  })

  const formik = useFormik({
    initialValues: {
      password: '',
      password_confirmation: '',
    },
    validationSchema: newPasswordSchema,
    onSubmit: async (values, {setSubmitting, setFieldError}) => {
      if (!token) return
      setSubmitError(undefined)
      try {
        setSubmitting(true)
        const config = {cancelToken: newCancelToken()}
        const payload = {token, ...values}

        if (isTradeShowAccount) {
          const login: any = await AuthService.tradeShowNewPassword(payload, config)
          await saveAuth(login)
          const {data} = await CommonService.getUser(config)
          await handleSaveCurrentUser(data)
          await navigate('/a1-batches')
          return
        }

        await AuthService.newPassword(payload, config)
        setSubmitted(true)
      } catch (error: any) {
        if (isCancel(error)) return
        const responseData = error?.response?.data
        // 422 field validation errors (including the "reused last 4 passwords"
        // business rule, which is also implemented as a ValidationException):
        // { message, errors: { password: [...] } }
        const passwordErrors = responseData?.errors?.password
        if (passwordErrors?.length) {
          setFieldError(
            'password',
            passwordErrors.map((err: string) => formatApiFieldError(intl, err)).join(' ')
          )
          return
        }
        // 400 business errors (invalid/expired token, etc.):
        // { status: 'ERROR', message }
        setSubmitError(responseData?.message || intl.formatMessage({id: 'SOMETHING_WENT_WRONG'}))
      } finally {
        setSubmitting(false)
      }
    },
  })

  if (tokenStatus === 'verifying') {
    return null
  }

  if (tokenStatus === 'invalid' || tokenStatus === 'expired') {
    return (
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <div className='text-center mb-10'>
            <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'NEW_PASSWORD'})}</h1>
            <div className='alert alert-danger'>
              <div className='alert-text font-weight-bold'>
                {intl.formatMessage({
                  id:
                    tokenStatus === 'expired'
                      ? 'INVALID_CONFIRMATION_TOKEN_EXPIRED'
                      : 'INVALID_CONFIRMATION_TOKEN',
                })}
              </div>
            </div>
          </div>
          <div className='d-flex flex-wrap justify-content-center'>
            <Link to='/auth/forgot-password'>
              <Button
                className='btn btn-lg btn-primary fw-bolder me-4'
                label={intl.formatMessage({id: 'RESET_PASSWORD'})}
              />
            </Link>
            <Link to='/auth/login'>
              <Button
                className='btn btn-lg btn-light-primary fw-bolder'
                label={intl.formatMessage({id: 'LOGIN'})}
              />
            </Link>
          </div>
        </div>
      </CSSTransition>
    )
  }

  if (submitted) {
    return (
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <div className='text-center mb-10'>
            <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'NEW_PASSWORD'})}</h1>
            <div className='alert alert-success'>
              <div className='alert-text font-weight-bold'>
                {intl.formatMessage({id: 'YOU_HAVE_SUCCESSFULLY_RESET_YOUR_PASSWORD'})}
              </div>
            </div>
          </div>
          <div className='d-flex flex-wrap justify-content-center'>
            <Link to='/auth/login'>
              <Button
                className='btn btn-lg btn-primary fw-bolder'
                label={intl.formatMessage({id: 'LOGIN'})}
              />
            </Link>
          </div>
        </div>
      </CSSTransition>
    )
  }

  return (
    <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
      <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
        <div className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'>
          <div className='text-center mb-10'>
            <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'NEW_PASSWORD'})}</h1>
            <div className='text-gray-500 fw-semibold fs-6'>
              {intl.formatMessage({id: 'ALREADY_HAVE_RESET_YOUR_PASSWORD'})}{' '}
              <Link to='/auth/login' className='link-primary fw-bolder'>
                {intl.formatMessage({id: 'SIGN_IN_HERE'})}
              </Link>
            </div>
          </div>
          {submitError && (
            <div className='mb-lg-10 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{submitError}</div>
            </div>
          )}
          <div className='fv-row mb-10 fv-plugins-icon-container'>
            <InputPassword
              labelClassName='fw-bolder text-dark fs-6 mb-0'
              label={intl.formatMessage({id: 'PASSWORD'})}
              formik={formik}
              name='password'
              required
              strengthMessage={intl.formatMessage({id: 'PASSWORD_STRENGTH_MESSAGE'})}
            />
          </div>
          <div className='fv-row mb-10 fv-plugins-icon-container'>
            <InputPassword
              labelClassName='fw-bolder text-dark fs-6 mb-0'
              label={intl.formatMessage({id: 'CONFIRM_PASSWORD'})}
              openHighlight={false}
              formik={formik}
              name='password_confirmation'
              required
            />
          </div>
          <div className='text-center'>
            <Button
              className='btn btn-lg btn-primary w-100 mb-5'
              label={intl.formatMessage({id: 'SUBMIT'})}
              loading={formik.isSubmitting}
              disabled={formik.isSubmitting}
              event={formik.submitForm}
            />
          </div>
        </div>
      </div>
    </CSSTransition>
  )
}
